import {
  CommentOutlined,
  DeleteOutlined,
  EditOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { Button, Input, Modal } from "antd";
import { FC, useEffect, useState } from "react";
import type {
  TaskNodeDetailComment,
  TaskNodeDetailReply,
  TaskNodeDetailTask,
} from "../TaskNodeDetailModal";
import "./index.css";

interface TaskCommentRepliesProps {
  canComment: boolean;
  comment: TaskNodeDetailComment;
  currentUserId: string;
  formatTime: (timestamp: number) => string;
  getMemberName: (userId: string) => string;
  task: TaskNodeDetailTask;
  onAddReply: (
    task: TaskNodeDetailTask,
    comment: TaskNodeDetailComment,
    content: string
  ) => Promise<boolean>;
  onDeleteReply: (
    task: TaskNodeDetailTask,
    comment: TaskNodeDetailComment,
    reply: TaskNodeDetailReply
  ) => Promise<boolean>;
  onUpdateReply: (
    task: TaskNodeDetailTask,
    comment: TaskNodeDetailComment,
    reply: TaskNodeDetailReply,
    content: string
  ) => Promise<boolean>;
}

const TaskCommentReplies: FC<TaskCommentRepliesProps> = ({
  canComment,
  comment,
  currentUserId,
  formatTime,
  getMemberName,
  task,
  onAddReply,
  onDeleteReply,
  onUpdateReply,
}) => {
  const [composing, setComposing] = useState(false);
  const [editingReply, setEditingReply] = useState<TaskNodeDetailReply | null>(
    null
  );
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setComposing(false);
    setEditingReply(null);
    setContent("");
  }, [comment.comment_id]);

  const replies = [...(comment.replies || [])].sort(
    (prev, next) => prev.created_at - next.created_at
  );

  function openReplyEditor() {
    setEditingReply(null);
    setContent("");
    setComposing(true);
  }

  function openEditEditor(reply: TaskNodeDetailReply) {
    setEditingReply(reply);
    setContent(reply.content);
    setComposing(true);
  }

  function closeEditor() {
    setComposing(false);
    setEditingReply(null);
    setContent("");
  }

  async function submitReply() {
    const normalizedContent = content.trim();
    if (!normalizedContent || submitting) return;
    setSubmitting(true);
    try {
      const succeeded = editingReply
        ? await onUpdateReply(task, comment, editingReply, normalizedContent)
        : await onAddReply(task, comment, normalizedContent);
      if (succeeded) closeEditor();
    } finally {
      setSubmitting(false);
    }
  }

  function confirmDeleteReply(reply: TaskNodeDetailReply) {
    Modal.confirm({
      maskClosable: false,
      keyboard: false,
      centered: true,
      className: "taskCommentReplyConfirm",
      title: "删除这条回复？",
      content: "删除后无法恢复，只会删除你自己发布的这条回复。",
      okText: "确认删除",
      cancelText: "取消",
      okButtonProps: { danger: true },
      onOk: () => onDeleteReply(task, comment, reply),
    });
  }

  return (
    <div className="taskCommentReplies">
      <div className="taskCommentReplies__bar">
        <span>
          <CommentOutlined />
          {replies.length ? `${replies.length} 条回复` : "暂无回复"}
        </span>
        {canComment && !composing && (
          <Button
            aria-label="回复批注"
            icon={<CommentOutlined />}
            onClick={openReplyEditor}
            size="small"
            type="text"
          >
            回复
          </Button>
        )}
      </div>

      {replies.length > 0 && (
        <div className="taskCommentReplies__list">
          {replies.map((reply) => {
            const isOwnReply = reply.author === currentUserId;
            return (
              <article key={reply.reply_id}>
                <div className="taskCommentReplies__avatar">
                  {getMemberName(reply.author).slice(0, 1)}
                </div>
                <div className="taskCommentReplies__replyBody">
                  <header>
                    <div>
                      <strong>{getMemberName(reply.author)}</strong>
                      <time>
                        {formatTime(reply.updated_at || reply.created_at)}
                      </time>
                    </div>
                    {isOwnReply && canComment && (
                      <div className="taskCommentReplies__actions">
                        <Button
                          aria-label="编辑回复"
                          icon={<EditOutlined />}
                          onClick={() => openEditEditor(reply)}
                          size="small"
                          type="text"
                        />
                        <Button
                          aria-label="删除回复"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => confirmDeleteReply(reply)}
                          size="small"
                          type="text"
                        />
                      </div>
                    )}
                  </header>
                  <p>{reply.content}</p>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {canComment && composing && (
        <div className="taskCommentReplies__editor">
          <Input.TextArea
            autoFocus
            maxLength={2000}
            onChange={(event) => setContent(event.target.value)}
            placeholder={
              editingReply ? "修改回复内容" : "回复这条批注，最多 2000 字"
            }
            rows={3}
            value={content}
          />
          <div>
            <span>{content.trim().length} / 2000</span>
            <Button disabled={submitting} onClick={closeEditor}>
              取消
            </Button>
            <Button
              aria-label={editingReply ? "保存回复" : "发送回复"}
              disabled={!content.trim()}
              icon={<SendOutlined />}
              loading={submitting}
              onClick={submitReply}
              type="primary"
            >
              {editingReply ? "保存回复" : "发送回复"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskCommentReplies;
